import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Image as ImageIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { uploadMedia } from '../../lib/storage';
import { UserMentionInput } from '../ui/UserMentionInput';
import { createNotification } from '../../lib/notifications';
import { notifyFollowers } from '../../lib/follows';
import { GifPicker } from '../ui/GifPicker';
import { GifIcon } from '../ui/GifIcon';

interface CreatePostFormProps {
  communityId: string;
  onSuccess: (postId?: string) => void;
}

export function CreatePostForm({ communityId, onSuccess }: CreatePostFormProps) {
  const { t } = useTranslation();
  const [content, setContent] = useState('');
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [gifUrls, setGifUrls] = useState<string[]>([]);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [mentionedUsers, setMentionedUsers] = useState<string[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Get current user on mount
  React.useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('users')
          .select('username, avatar_url')
          .eq('id', user.id)
          .single();

        setCurrentUser({
          id: user.id,
          username: profile?.username,
          avatar_url: profile?.avatar_url
        });
      }
    };
    getUser();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() && mediaFiles.length === 0 && gifUrls.length === 0) return;

    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload images/videos if any (GIF URLs need no upload)
      const mediaUrls = await Promise.all(
        mediaFiles.map(async (file) => {
          const isVideo = file.type.startsWith('video/');
          const bucket = isVideo ? 'post-videos' : 'post-images';
          
          try {
            return await uploadMedia(file, bucket);
          } catch (error) {
            console.error('Error uploading file:', error);
            setError(error.message || 'Failed to upload file');
            throw error;
          }
        })
      );

      // Create post (combine GIF URLs with uploaded media)
      const allImages = [...gifUrls, ...mediaUrls.filter(Boolean)];
      const { data: postData, error: insertError } = await supabase
        .from('community_posts')
        .insert([{
          community_id: communityId,
          user_id: user.id,
          content,
          images: allImages
        }])
        .select('id')
        .single();

      if (insertError) throw insertError;

      // Send notifications to mentioned users
      if (mentionedUsers.length > 0) {
        const notifications = mentionedUsers.map(userId => ({
          userId,
          type: 'mention',
          title: 'You were mentioned',
          message: `${currentUser?.username || 'Someone'} mentioned you in a community post`,
          data: {
            post_type: 'community',
            community_id: communityId,
            sender_id: user.id,
            sender_username: currentUser?.username,
            sender_avatar_url: currentUser?.avatar_url,
            content_preview: content.substring(0, 100)
          }
        }));

        await Promise.all(
          notifications.map(notification => createNotification(notification))
        );
      }

      // Notify followers about the new community post
      notifyFollowers({
        userId: user.id,
        username: currentUser?.username || 'Someone',
        avatarUrl: currentUser?.avatar_url,
        type: 'follower_new_post',
        title: 'New Community Post',
        message: `${currentUser?.username || 'Someone'} shared a post in a community`,
        data: {
          post_type: 'community',
          post_id: postData?.id,
          community_id: communityId,
          content_preview: content.substring(0, 100)
        }
      });

      setContent('');
      setMediaFiles([]);
      setGifUrls([]);
      setMentionedUsers([]);
      
      // Refocus the textarea for better UX with delay
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 100);
      
      onSuccess(postData?.id);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
      // Ensure focus is restored even if there was an error
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 150);
    }
  };

  const handleMediaClick = () => {
    fileInputRef.current?.click();
  };

  const totalMediaCount = mediaFiles.length + gifUrls.length;

  const handleMediaChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setError(''); // Clear any previous errors
    
    if (files.length + totalMediaCount > 4) {
      setError(t('forms.post.maxMediaFiles'));
      return;
    }
    
    // Validate files
    for (const file of files) {
      // Check file type first
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        setError(t('forms.post.onlyImageVideo'));
        return;
      }
      
      if (file.type.startsWith('video/')) {
        // Check video file size (max 50MB for videos)
        if (file.size > 50 * 1024 * 1024) {
          setError(t('forms.post.videoSizeLimit'));
          return;
        }
      } else if (file.type.startsWith('image/')) {
        // Check image file size (max 10MB for images)
        if (file.size > 10 * 1024 * 1024) {
          setError(t('forms.post.imageSizeLimit'));
          return;
        }
      }
    }
    
    setMediaFiles(prev => [...prev, ...files]);
  };

  const addGif = (url: string) => {
    if (totalMediaCount >= 4) {
      setError(t('forms.post.maxMediaFiles'));
      return;
    }
    setGifUrls(prev => [...prev, url]);
    setError('');
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
      <form onSubmit={handleSubmit}>
        <UserMentionInput
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onMentionedUsersChange={setMentionedUsers}
          placeholder={t('forms.post.sharePlaceholder')}
          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-accent-text/20 focus:border-accent-text resize-none"
        />

        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/*,video/*"
          multiple
          onChange={handleMediaChange}
        />

        {(mediaFiles.length > 0 || gifUrls.length > 0) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {gifUrls.map((url, index) => (
              <div key={`gif-${index}`} className="relative">
                <img
                  src={url}
                  alt={`GIF ${index + 1}`}
                  className="h-16 w-16 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => setGifUrls(prev => prev.filter((_, i) => i !== index))}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                >
                  ×
                </button>
              </div>
            ))}
            {mediaFiles.map((file, index) => (
              <div key={index} className="relative">
                {file.type.startsWith('video/') ? (
                  <video
                    src={URL.createObjectURL(file)}
                    className="h-16 w-16 object-cover rounded-lg"
                    muted
                    loop
                    autoPlay
                  />
                ) : (
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Preview ${index + 1}`}
                    className="h-16 w-16 object-cover rounded-lg"
                  />
                )}
                <button
                  type="button"
                  onClick={() => setMediaFiles(prev => prev.filter((_, i) => i !== index))}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="mt-2 text-red-500 text-sm">{error}</div>
        )}

        <GifPicker
          isOpen={showGifPicker}
          onClose={() => setShowGifPicker(false)}
          onSelect={addGif}
        />

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShowGifPicker(true)}
              disabled={totalMediaCount >= 4}
              className="p-2 text-gray-500 hover:text-accent-text rounded-lg hover:bg-gray-100 disabled:opacity-50 transition-colors"
              title="Add GIF"
            >
              <GifIcon className="h-5 w-5 text-current" />
            </button>
            <button
              type="button"
              onClick={handleMediaClick}
              disabled={totalMediaCount >= 4}
              className="p-2 text-gray-500 hover:text-accent-text rounded-lg hover:bg-gray-100 disabled:opacity-50 transition-colors"
              title={t('forms.post.addMedia', 'Add photo or video')}
            >
              <ImageIcon className="h-5 w-5" />
            </button>
          </div>
          <button
            type="submit"
            disabled={loading || (!content.trim() && mediaFiles.length === 0 && gifUrls.length === 0)}
            className="px-4 py-2 bg-accent-text text-white rounded-lg hover:bg-accent-text/90 disabled:opacity-50 transition-colors"
          >
            {loading ? t('forms.post.posting') : t('forms.post.post')}
          </button>
        </div>
      </form>
    </div>
  );
}